import { useTokens } from "@/design-system/hooks/useTokens";
import { ButtonProps } from "./types";

/**
 * 通用按鈕組件
 * 使用設計 Token 系統來確保視覺一致性
 */
export function Button({
  children,
  variant = "primary",
  size = "medium",
  fullWidth = false,
  isLoading = false,
  startIcon,
  endIcon,
  className = "",
  disabled,
  ...rest
}: ButtonProps) {
  const tokens = useTokens();

  // 根據變體設定按鈕樣式
  const getVariantStyles = () => {
    switch (variant) {
      case "primary":
        return {
          backgroundColor: tokens.colors.primary.main,
          color: tokens.colors.primary.contrastText,
          border: "none",
          hoverBg: tokens.colors.primary.dark,
          activeBg: tokens.colors.primary.dark,
          disabledBg: tokens.colors.gray[300],
          disabledColor: tokens.colors.gray[500],
        };
      case "secondary":
        return {
          backgroundColor: tokens.colors.secondary.main,
          color: tokens.colors.secondary.contrastText,
          border: "none",
          hoverBg: tokens.colors.secondary.dark,
          activeBg: tokens.colors.secondary.dark,
          disabledBg: tokens.colors.gray[300],
          disabledColor: tokens.colors.gray[500],
        };
      case "outlined":
        return {
          backgroundColor: "transparent",
          color: tokens.colors.primary.main,
          border: `1px solid ${tokens.colors.primary.main}`,
          hoverBg: tokens.colors.primary[50],
          activeBg: tokens.colors.primary[100],
          disabledBg: "transparent",
          disabledColor: tokens.colors.gray[400],
          disabledBorder: `1px solid ${tokens.colors.gray[300]}`,
        };
      case "text":
        return {
          backgroundColor: "transparent",
          color: tokens.colors.primary.main,
          border: "none",
          hoverBg: tokens.colors.primary[50],
          activeBg: tokens.colors.primary[100],
          disabledBg: "transparent",
          disabledColor: tokens.colors.gray[400],
        };
      default:
        return {
          backgroundColor: tokens.colors.primary.main,
          color: tokens.colors.primary.contrastText,
          border: "none",
          hoverBg: tokens.colors.primary.dark,
          activeBg: tokens.colors.primary.dark,
          disabledBg: tokens.colors.gray[300],
          disabledColor: tokens.colors.gray[500],
        };
    }
  };

  // 根據尺寸設定按鈕尺寸和填充
  const getSizeStyles = () => {
    switch (size) {
      case "small":
        return {
          padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
          fontSize: tokens.typography.size.sm,
          borderRadius: tokens.radius.md,
        };
      case "large":
        return {
          padding: `${tokens.spacing.md} ${tokens.spacing.lg}`,
          fontSize: tokens.typography.size.lg,
          borderRadius: tokens.radius.lg,
        };
      default: // medium
        return {
          padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
          fontSize: tokens.typography.size.md,
          borderRadius: tokens.radius.md,
        };
    }
  };

  const variantStyles = getVariantStyles();
  const sizeStyles = getSizeStyles();

  // 建立按鈕樣式
  const buttonStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: tokens.typography.weight.medium,
    width: fullWidth ? "100%" : "auto",
    cursor: disabled || isLoading ? "not-allowed" : "pointer",
    transition: tokens.transitions.normal,
    ...sizeStyles,
    ...variantStyles,
    ...(disabled && {
      backgroundColor: variantStyles.disabledBg,
      color: variantStyles.disabledColor,
      border: variantStyles.disabledBorder || variantStyles.border,
      cursor: "not-allowed",
    }),
  };

  // 將樣式轉換為 Tailwind 類名
  const getTailwindClasses = () => {
    let classes = `
      inline-flex items-center justify-center
      font-medium
      transition-all
      ${fullWidth ? "w-full" : ""}
      ${disabled || isLoading ? "cursor-not-allowed" : "cursor-pointer"}
    `;

    // 根據尺寸添加類
    if (size === "small") {
      classes += " text-sm px-2 py-1 rounded";
    } else if (size === "large") {
      classes += " text-lg px-6 py-4 rounded-lg";
    } else {
      classes += " text-base px-4 py-2 rounded";
    }

    // 根據變體添加類
    if (variant === "primary") {
      classes += disabled
        ? " bg-gray-300 text-gray-500"
        : " bg-primary-500 text-black hover:bg-primary-700 active:bg-primary-700";
    } else if (variant === "secondary") {
      classes += disabled
        ? " bg-gray-300 text-gray-500"
        : " bg-secondary-500 text-white hover:bg-secondary-700 active:bg-secondary-700";
    } else if (variant === "outlined") {
      classes += disabled
        ? " bg-transparent text-gray-400 border border-gray-300"
        : " bg-transparent text-primary-500 border border-primary-500 hover:bg-primary-50 active:bg-primary-100";
    } else if (variant === "text") {
      classes += disabled
        ? " bg-transparent text-gray-400"
        : " bg-transparent text-primary-500 hover:bg-primary-50 active:bg-primary-100";
    }

    return classes;
  };

  return (
    <button
      className={`${getTailwindClasses()} ${className}`}
      style={buttonStyle}
      disabled={disabled || isLoading}
      {...rest}
    >
      {isLoading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4 text-current"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}

      {startIcon && !isLoading && <span className="mr-2">{startIcon}</span>}
      {children}
      {endIcon && <span className="ml-2">{endIcon}</span>}
    </button>
  );
}
